import { defineStore } from 'pinia'

const DEFAULT_THEME_ID = 1
const DEFAULT_THEME_CSS = '/css/theme-styles/theme-minimal.css'
const DEFAULT_THEME_FRAME = 'Default'

const decodeBase64Json = <T>(value: string): T => {
  const binary = atob(value)
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))

  return JSON.parse(new TextDecoder().decode(bytes)) as T
}

export const useThemeStyleStore = defineStore('themeStyle', () => {
  const themeId = ref(1)
  const css = ref('')
  const frame = ref('Default')
  const primaryColor = ref<{ red: number; green: number; blue: number }>({ red: 255, green: 255, blue: 255 })
  const secondaryColor = ref<{ red: number; green: number; blue: number }>({ red: 255, green: 255, blue: 255 })
  const font = ref<{ fontFamily: string; cdn: string }>({
    fontFamily: '',
    cdn: '',
  })
  const { $api } = useNuxtApp()
  const route = useRoute()

  const fetch = async () => {
    const query = route.query
    const previewThemeStyle = typeof query.themeStyle === 'string'
      ? decodeBase64Json<Partial<ThemeStyle> & { static?: ThemeStyle['static'] }>(query.themeStyle)
      : undefined

    let themeStyle: ThemeStyle | undefined
    try {
      const response = await $api<ApiSuccessResponse<ThemeStyle>>('/theme-style')
      themeStyle = response.data
    } catch (error) {
      if (!previewThemeStyle) {
        throw error
      }
    }

    if (previewThemeStyle) {
      update({
        ...themeStyle,
        ...previewThemeStyle,
        themeId: previewThemeStyle.themeId ?? themeStyle?.themeId ?? DEFAULT_THEME_ID,
        frame: previewThemeStyle.static ? previewThemeStyle.static.frame : (themeStyle?.static?.frame ?? DEFAULT_THEME_FRAME),
        css: previewThemeStyle.static ? previewThemeStyle.static.css : (themeStyle?.static?.css ?? DEFAULT_THEME_CSS),
      })
    } else if (themeStyle) {
      update({
        themeId: themeStyle.themeId ?? DEFAULT_THEME_ID,
        primaryColor: themeStyle.primaryColor,
        secondaryColor: themeStyle.secondaryColor,
        frame: themeStyle.static ? themeStyle.static.frame : DEFAULT_THEME_FRAME,
        css: themeStyle.static ? themeStyle.static.css : DEFAULT_THEME_CSS,
        font: {
          fontFamily: themeStyle.font?.fontFamily ?? '',
          cdn: themeStyle.font?.cdn ?? '',
        },
      })
    }
  }

  const update = (payload: ThemeStyle) => {
    themeId.value = payload.themeId ?? DEFAULT_THEME_ID
    css.value = payload.css || DEFAULT_THEME_CSS
    frame.value = payload.frame || DEFAULT_THEME_FRAME
    primaryColor.value = payload.primaryColor
    secondaryColor.value = payload.secondaryColor
    font.value = payload.font ?? { fontFamily: '', cdn: '' }
  }

  return {
    themeId,
    css,
    frame,
    primaryColor,
    secondaryColor,
    font,
    fetch,
    update,
  }
})
